import { CheckOutlined } from '@ant-design/icons';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { useSwitchOrganizationMutation } from '@app/api/Authentication';
import { useOrganizationSelectEffects } from '@app/components/OrganizationSelect/hooks/useOrganizationSelectEffects';
import {
  clearOrganizationSwitchTarget,
  readOrganizationSwitchTarget,
  writeOrganizationSwitchTarget,
} from '@app/components/OrganizationSelect/utils/switchTargetStorage';
import { WARNING } from '@app/constants';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { selectOrganizations, selectUser, useAppSelector } from '@app/store';
import { clearActiveBranchSession, purgePersistedRedux } from '@app/utils';

import {
  persistAuthorizationAccessToken,
  persistAuthorizationCookies,
} from '@pages/Authorization/utils/authorizationSession';

import type { MenuProps } from 'antd';

export const useOrganizationSelect = () => {
  const organizations = useAppSelector(selectOrganizations);
  const user = useAppSelector(selectUser);
  const { showNotification } = useContext(NotificationContext);
  const [switchOrganization] = useSwitchOrganizationMutation();

  // Имя целевой организации: и флаг «идёт переключение», и подпись в оверлее.
  // Инициализируется из sessionStorage — на случай, что мы уже после reload.
  const [switchingTo, setSwitchingTo] = useState<string | null>(
    readOrganizationSwitchTarget
  );
  // true — переключение запущено в этой сессии страницы и закончится
  // перезагрузкой (оверлей уедет вместе со страницей). false + switchingTo —
  // мы уже после reload и ждём новый профиль.
  const startedHereRef = useRef(false);

  const isSwitching = switchingTo !== null;
  const isAwaitingProfile = isSwitching && !startedHereRef.current;

  const resolveSwitch = useCallback(() => setSwitchingTo(null), []);

  useOrganizationSelectEffects({
    isAwaitingProfile,
    hasProfile: Boolean(user?.userId),
    onResolve: resolveSwitch,
  });

  const currentOrganization = useMemo(
    () => organizations.find((item) => item.IsCurrent) ?? null,
    [organizations]
  );

  const switchToOrganization = useCallback(
    async (organizationId: number, organizationName: string | null) => {
      if (isSwitching) {
        return;
      }

      const targetName = organizationName || 'другую организацию';
      // Оверлей поднимается синхронно и перекрывает интерфейс до перезагрузки.
      startedHereRef.current = true;
      setSwitchingTo(targetName);

      try {
        const result = await switchOrganization({ organizationId }).unwrap();

        // Переключение организации — по сути повторный вход в другую БД:
        // сохраняем новые токен и cookie, чистим сессию филиала и сохранённое
        // состояние Redux, кладём маркер для оверлея после reload и
        // перезагружаемся, чтобы права, филиалы, кэш и websocket перезапросились
        // уже под выбранной организацией. Живой стор не трогаем — resetStore
        // моргает пустым состоянием.
        persistAuthorizationCookies(result);
        persistAuthorizationAccessToken(result.AccessToken);
        clearActiveBranchSession();

        await purgePersistedRedux();

        writeOrganizationSwitchTarget(targetName);
        window.location.replace('/');
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
        startedHereRef.current = false;
        clearOrganizationSwitchTarget();
        setSwitchingTo(null);
      }
    },
    [isSwitching, showNotification, switchOrganization]
  );

  const menu: MenuProps = useMemo(
    () => ({
      items: organizations.map((item) => ({
        key: String(item.Id),
        label: item.Name || 'Организация без названия',
        disabled: isSwitching || item.IsCurrent,
        className: item.IsCurrent
          ? 'OrganizationSelect__menu-item--current'
          : undefined,
        icon: item.IsCurrent ? <CheckOutlined /> : undefined,
        onClick: item.IsCurrent
          ? undefined
          : () => switchToOrganization(item.Id, item.Name),
      })),
    }),
    [isSwitching, organizations, switchToOrganization]
  );

  return {
    currentOrganization,
    currentName: currentOrganization?.Name || 'Выбор организации',
    hasMultipleOrganizations: organizations.length >= 2,
    isSwitching,
    switchingTo,
    menu,
  };
};
